/* STEP: Serviceability.
   Forward = one Pickup network forking into Prepaid/COD Drop networks (3 lanes).
   Reverse (A3) = Clickpost two-tab layout: Drop Pincodes (Warehouse)[PREPAID] and
   Pickup Pincodes (End Customer)[PREPAID|EXCHANGE], with an Automatic Pincode Fetch
   toggle (default Inactive), per-pill uploaded-count + download-existing, and the
   exact dropzone copy. No COD lane in reverse. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import BoltIcon from '@mui/icons-material/Bolt'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import CheckIcon from '@mui/icons-material/Check'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {useToast, Badge} from '../../components'
import {Footer, scrollToFirstError} from './fieldKit'
import {apiConstants, get} from '../../../../common/constants'
import {downloadFile} from '../../../../common/util'

/* Real sample served by the backend (same file the bulk-serviceability module uses) */
function fetchSample(toast) {
  get(apiConstants.DOWNLOAD_SAMPLE_FILE, {fileName: 'BULK_SERVICEABILITY_PINCODE_SAMPLE'})
    .then(({data}) => {
      if (!data) throw new Error('empty file')
      downloadFile(data, 'Bulk_Serviceability_Pincode_Sample.csv')
      toast('Sample downloaded: Bulk_Serviceability_Pincode_Sample.csv', 'success')
    })
    .catch(() => toast('Unable to download the sample file.', 'error'))
}

/* ════════ FORWARD — 3 lanes ════════ */
const SLOTS = [
  {key: 'pickup', count: '29,412', title: 'Pickup serviceability', hero: true, chips: [['PICKUP', 'var(--primary-l)', 'var(--primary)'], ['ONE FILE · COVERS PREPAID + COD', '#EAF6EC', 'var(--success)']], desc: 'Pincodes the courier can pick up from. Pickup capability does not depend on payment mode — a single file covers everything.'},
  {key: 'dropPrepaid', count: '27,886', title: 'Drop — Prepaid', chips: [['DROP · PREPAID', 'var(--primary-l)', 'var(--primary)']], desc: 'Pincodes deliverable for prepaid orders.'},
  {key: 'dropCod', count: '21,304', title: 'Drop — COD', chips: [['DROP · COD', '#FFF3D6', '#B7791F']], desc: 'Pincodes with cash-on-delivery coverage — usually a subset of prepaid.'},
]

function Lane({slot, value, invalid, onTake, onReplace, onSample}) {
  const [drag, setDrag] = useState(false)
  return (
    <div className={'field svc-lane' + (slot.hero ? ' svc-hero' : '') + (invalid ? ' invalid' : '')} style={{margin: 0}}>
      <div className='row center gap8' style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap'}}>
        {slot.chips.map((c) => (
          <span key={c[0]} className='svc-chip' style={{background: c[1], color: c[2]}}>
            {c[0]}
          </span>
        ))}
      </div>
      <div style={{fontWeight: 700, fontSize: 13.5}}>{slot.title}</div>
      <div className='muted' style={{fontSize: 12, margin: '2px 0 10px'}}>
        {slot.desc}
      </div>
      {value ? (
        <div className='svc-done'>
          <span className='svc-stamp'>
            <CheckIcon style={{width: 14, height: 14}} />
          </span>
          <div style={{minWidth: 0}}>
            <div style={{fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{value}</div>
            <div className='muted' style={{fontSize: 11.5}}>
              {slot.count} pincodes detected · columns match the template
            </div>
          </div>
          <button type='button' className='btn btn-sm' style={{marginLeft: 'auto', flexShrink: 0}} onClick={onReplace}>
            Replace
          </button>
        </div>
      ) : (
        <label
          className={'svc-zone' + (drag ? ' drag' : '')}
          style={{display: 'block'}}
          onDragOver={(e) => {
            e.preventDefault()
            setDrag(true)
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDrag(false)
            const f = e.dataTransfer.files && e.dataTransfer.files[0]
            if (f) onTake(f)
          }}
        >
          <CloudUploadOutlinedIcon style={{width: 20, height: 20}} />
          <div style={{fontWeight: 600, fontSize: 12.5, marginTop: 6}}>
            Drop the file here, or <span style={{color: 'var(--primary)', textDecoration: 'underline'}}>browse</span>
          </div>
          <div className='muted' style={{fontSize: 11, marginTop: 3}}>
            .csv / .xlsx · columns: pincode, city, state, serviceable
          </div>
          <input type='file' accept='.csv,.xlsx' style={{display: 'none'}} onChange={(e) => e.target.files[0] && onTake(e.target.files[0])} />
        </label>
      )}
      <div style={{marginTop: 9}}>
        <button type='button' className='btn btn-sm' onClick={onSample}>
          <DownloadOutlinedIcon style={{width: 14, height: 14}} />
          Sample template
        </button>
      </div>
      <div className='vmsg'>Required.</div>
    </div>
  )
}

Lane.propTypes = {slot: PropTypes.object, value: PropTypes.string, invalid: PropTypes.bool, onTake: PropTypes.func, onReplace: PropTypes.func, onSample: PropTypes.func}

function ForwardServ({W, dispatch, goBack, goNext}) {
  const toast = useToast()
  const SVC = W.SVC
  const [inv, setInv] = useState({})
  const take = (key, file) => {
    /* keep the display name in SVC and the actual File for the commit upload */
    dispatch({type: 'FIELD', slice: 'SVC', key, value: file.name})
    dispatch({type: 'FIELD', slice: 'SVCFILES', key, value: file})
    setInv((p) => ({...p, [key]: false}))
    toast('Serviceability file attached.', 'success')
  }
  const clear = (key) => {
    dispatch({type: 'FIELD', slice: 'SVC', key, value: ''})
    dispatch({type: 'FIELD', slice: 'SVCFILES', key, value: null})
  }
  const sample = () => fetchSample(toast)
  const n = SLOTS.filter((s) => SVC[s.key]).length
  const onNext = () => {
    const miss = ['pickup', 'dropPrepaid', 'dropCod'].filter((k) => !SVC[k])
    if (miss.length) {
      const e = {}
      miss.forEach((k) => (e[k] = true))
      setInv(e)
      scrollToFirstError()
      return
    }
    goNext()
  }

  return (
    <div className='card card-pad' style={{maxWidth: 1400}}>
      <div className='row center' style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4}}>
        <h3 style={{margin: 0}}>Serviceability Upload</h3>
        <span style={{marginLeft: 'auto'}}>
          {n === 3 ? <Badge status='active' label='All 3 files ready' /> : <Badge status={n ? 'info' : 'pending'} label={n + ' of 3 files'} />}
        </span>
      </div>
      <p className='sub' style={{margin: '0 0 16px'}}>
        One pickup network → two drop networks. Pickup is payment-mode agnostic; drop coverage differs for Prepaid and COD.
      </p>
      <Lane slot={SLOTS[0]} value={SVC.pickup} invalid={inv.pickup} onTake={(f) => take('pickup', f)} onReplace={() => clear('pickup')} onSample={sample} />
      <div className='svc-fork' aria-hidden='true'>
        <svg viewBox='0 0 600 48' preserveAspectRatio='none'>
          <path className='fp' d='M300 2 V12 C300 22 288 24 272 24 L182 24 C162 24 152 30 152 38 V48' />
          <path className='fp' d='M300 2 V12 C300 22 312 24 328 24 L418 24 C438 24 448 30 448 38 V48' />
        </svg>
      </div>
      <div className='svc-drops'>
        <Lane slot={SLOTS[1]} value={SVC.dropPrepaid} invalid={inv.dropPrepaid} onTake={(f) => take('dropPrepaid', f)} onReplace={() => clear('dropPrepaid')} onSample={sample} />
        <Lane slot={SLOTS[2]} value={SVC.dropCod} invalid={inv.dropCod} onTake={(f) => take('dropCod', f)} onReplace={() => clear('dropCod')} onSample={sample} />
      </div>
      <Footer step={W.step} onBack={goBack} onNext={onNext} />
    </div>
  )
}

/* ════════ REVERSE — Clickpost two-tab ════════ */
const TABS = [
  {id: 'drop', label: 'Drop Pincodes (Warehouse)', pills: [{id: 'prepaid', label: 'PREPAID', lane: 'dropWarehousePrepaid', count: 9541}]},
  {
    id: 'pickup',
    label: 'Pickup Pincodes (End Customer)',
    pills: [
      {id: 'prepaid', label: 'PREPAID', lane: 'pickupEndPrepaid', count: 27640},
      {id: 'exchange', label: 'EXCHANGE', lane: 'pickupEndExchange', count: 4120},
    ],
  },
]
const LANES = ['dropWarehousePrepaid', 'pickupEndPrepaid', 'pickupEndExchange']

function ReverseServ({W, dispatch, goBack, goNext}) {
  const toast = useToast()
  const SVC = W.SVC
  const UI = W.SVCUI
  const [drag, setDrag] = useState(false)
  const laneReady = (id) => UI.autoFetch || !!SVC[id]
  const t = TABS.find((x) => x.id === UI.tab) || TABS[0]
  const pill = t.pills.find((p) => p.id === UI.pill) || t.pills[0]
  const lane = pill.lane
  const v = SVC[lane]
  const readyN = LANES.filter(laneReady).length

  const setUI = (patch) => dispatch({type: 'PATCH', slice: 'SVCUI', value: patch})
  const take = (file) => {
    dispatch({type: 'FIELD', slice: 'SVC', key: lane, value: file.name})
    dispatch({type: 'FIELD', slice: 'SVCFILES', key: lane, value: file})
    toast(pill.label + ' — ' + t.label + ' uploaded.', 'success')
  }

  const onNext = () => {
    if (UI.autoFetch) {
      goNext()
      return
    }
    const miss = LANES.filter((id) => !laneReady(id))
    if (miss.length) {
      toast('Upload all three reverse lanes (Drop·Prepaid, Pickup·Prepaid, Pickup·Exchange), or enable Automatic Pincode Fetch.', 'error')
      return
    }
    goNext()
  }

  return (
    <div className='card card-pad' style={{maxWidth: 1400}}>
      <div className='svc-rhead'>
        <div>
          <h3 style={{margin: 0}}>Serviceability</h3>
          <p className='sub' style={{margin: '4px 0 0', maxWidth: 520}}>
            Upload details on the pin codes you require to be served by the selected carrier partner, or use the automated service provided.
          </p>
        </div>
        <button
          type='button'
          className={'svc-toggle' + (UI.autoFetch ? ' on' : '')}
          title='When active, ProShip fetches serviceable pincodes automatically — no manual upload needed.'
          onClick={() => {
            setUI({autoFetch: !UI.autoFetch})
            toast(!UI.autoFetch ? 'Automatic Pincode Fetch enabled — manual upload paused.' : 'Automatic Pincode Fetch disabled.', 'success')
          }}
        >
          Automatic Pincode Fetch <InfoOutlinedIcon style={{width: 13, height: 13}} />
          <span className='sw' />
          <span style={{fontWeight: 700}}>{UI.autoFetch ? 'Active' : 'Inactive'}</span>
        </button>
      </div>

      <div className='row gap8' style={{display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10}}>
        <span style={{marginTop: 2, flexShrink: 0}}>
          {UI.autoFetch ? (
            <Badge status='info' label='Auto-fetch on' />
          ) : readyN === 3 ? (
            <Badge status='active' label='All 3 lanes ready' />
          ) : (
            <Badge status={readyN ? 'info' : 'pending'} label={readyN + ' of 3 lanes'} />
          )}
        </span>
        <span className='warnbox info' style={{margin: 0, padding: '6px 10px', fontSize: 11.5, flex: 1}}>
          <InfoOutlinedIcon style={{width: 15, height: 15}} />
          <div>
            <b>Reverse (RVP):</b> Drop = warehouse / return destination; Pickup = end-customer door. Lanes: Drop·Prepaid, Pickup·Prepaid, Pickup·Exchange (no COD in reverse).
          </div>
        </span>
      </div>

      <div className='svc-rtabs'>
        {TABS.map((tb) => {
          const on = tb.id === UI.tab
          const done = tb.pills.every((pp) => laneReady(pp.lane))
          return (
            <button key={tb.id} type='button' className={'svc-rtab' + (on ? ' on' : '')} onClick={() => setUI({tab: tb.id, moreOpen: false})}>
              {tb.label}
              {done ? <CheckIcon style={{width: 13, height: 13}} /> : null}
            </button>
          )
        })}
      </div>

      <div className='svc-rpills'>
        {t.pills.map((pp) => {
          const on = pp.id === UI.pill
          return (
            <button key={pp.id} type='button' className={'svc-rpill' + (on ? ' on' : '')} onClick={() => setUI({pill: pp.id, moreOpen: false})}>
              {pp.label}
              {laneReady(pp.lane) ? <span className='svc-rdot' /> : null}
            </button>
          )
        })}
      </div>

      {UI.autoFetch ? (
        <div className='warnbox info' style={{margin: 0}}>
          <BoltIcon style={{width: 16, height: 16}} />
          <div>
            <b>Automatic Pincode Fetch is active.</b> ProShip pulls serviceable pincodes from the carrier automatically — manual upload is paused for all lanes. Switch the toggle off to upload files instead.
          </div>
        </div>
      ) : (
        <div className='svc-rpanel'>
          <div className='svc-rpanel-h'>
            <div className='svc-rcount'>
              Pincodes uploaded in {pill.label} <b>{pill.count.toLocaleString('en-IN')}</b>

            </div>
          </div>
          {v ? (
            <div className='svc-done'>
              <span className='svc-stamp'>
                <CheckIcon style={{width: 14, height: 14}} />
              </span>
              <div style={{minWidth: 0}}>
                <div style={{fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{v}</div>
                <div className='muted' style={{fontSize: 11.5}}>
                  {pill.label} · {t.label} · columns match the template
                </div>
              </div>
              <button
                type='button'
                className='btn btn-sm'
                style={{marginLeft: 'auto', flexShrink: 0}}
                onClick={() => {
                  dispatch({type: 'FIELD', slice: 'SVC', key: lane, value: ''})
                  dispatch({type: 'FIELD', slice: 'SVCFILES', key: lane, value: null})
                }}
              >
                Replace
              </button>
            </div>
          ) : (
            <label
              className={'svc-rdz' + (drag ? ' drag' : '')}
              style={{display: 'block'}}
              onDragOver={(e) => {
                e.preventDefault()
                setDrag(true)
              }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDrag(false)
                const f = e.dataTransfer.files && e.dataTransfer.files[0]
                if (f) take(f)
              }}
            >
              <CloudUploadOutlinedIcon style={{width: 22, height: 22}} />
              <div style={{marginTop: 8, fontSize: 13.5}}>
                <span className='svc-rlink' onClick={(e) => e.preventDefault()}>
                  Select File
                </span>{' '}
                <span className='muted'>or drag files here</span>
              </div>
              <div style={{marginTop: 6}}>
                <span
                  className='svc-rlink'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    fetchSample(toast)
                  }}
                >
                  <DownloadOutlinedIcon style={{width: 13, height: 13}} /> Download Sample
                </span>
              </div>
              <ul className='svc-rbul'>
                <li>Max rows: 50,000</li>
                <li>Processing may take ~5 min for files up to 500KB</li>
              </ul>
              <button
                type='button'
                className='svc-rmore'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setUI({moreOpen: !UI.moreOpen})
                }}
              >
                More Instructions {UI.moreOpen ? <ExpandMoreIcon style={{width: 13, height: 13}} /> : <ChevronRightIcon style={{width: 13, height: 13}} />}
              </button>
              {UI.moreOpen ? (
                <div className='svc-rmore-body'>
                  Use the exact column headers from the sample. One pincode per row. Duplicate pincodes are de-duplicated on import. Files over 500KB are processed asynchronously — you can leave this step and return.
                </div>
              ) : null}
              <input type='file' accept='.csv,.xlsx' style={{display: 'none'}} onChange={(e) => e.target.files[0] && take(e.target.files[0])} />
            </label>
          )}
        </div>
      )}

      <Footer step={W.step} onBack={goBack} onNext={onNext} />
    </div>
  )
}

export default function StepServ(props) {
  return props.W.S.direction === 'Reverse' ? <ReverseServ {...props} /> : <ForwardServ {...props} />
}

StepServ.propTypes = {
  W: PropTypes.object.isRequired,
}
ForwardServ.propTypes = ReverseServ.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
}
